/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// src/components/ResourceManager/Datacenter/DcInventoryPage.tsx

import React, { useState, useEffect, useMemo } from "react";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react"; // Para obtener el token
import { columns as baseColumns, DataCenter, shapeDataCenterData } from "./DcData";

// Importamos las funciones del request
import {
	getDataCenters,
	addDataCenter,
	updateDataCenter,
	deleteDataCenter,
	AddDataCenterPayload,
	getProviderIds,
	getDataCenterId,
} from "./request/request";

// UI components
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import { buildPredicate, FilterField, labelId, ParsedFilter as OldParsed, parseToken } from "../../../../helpers/advancedFilters";
import { useAppSelector } from "@redux/hooks";

// Modals
import { DcViewModal, DcEditModal, ProviderOption } from "./DcModals";

// Icons
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";

export default function DatacenterPage() {
	// ========= 1) Table data  =========
	const [dataCenters, setDataCenters] = useState<DataCenter[]>([]);

	/**
	 * isFetching: usaremos esta misma variable de estado para
	 *  1) mostrar spinner durante el GET inicial
	 *  2) y también para mostrar spinner mientras ejecutamos ADD/EDIT/DELETE
	 */
	const [isFetching, setIsFetching] = useState(false);

	// ========= 2) Add/Edit modal state & fields =========
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [editDetailData, setEditDetailData] = useState<DataCenter | null>(null);
	const [isEditLoading, setIsEditLoading] = useState(false);

	// Campos para Add/Edit
	const [dcId, setDcId] = useState(""); // datacenterId
	const [dcName, setDcName] = useState("");
	const [dcDirection, setDcDirection] = useState("");
	const [dcCapacity, setDcCapacity] = useState("");
	const [dcStatus, setDcStatus] = useState("Active");
	const [dcGps, setDcGps] = useState("");
	const [dcPhase, setDcPhase] = useState("");
	const [dcCage, setDcCage] = useState("");
	const [dcRackId, setDcRackId] = useState("");
	const [dcRackCapacity, setDcRackCapacity] = useState("");
	const [dcPatchPanelODF, setDcPatchPanelODF] = useState("");
	const [dcRackSecurityPin, setDcRackSecurityPin] = useState("");
	const [dcVisible, setDcVisible] = useState(false);
	const [dcProvider, setDcProvider] = useState("");
	const [dcProviderCode, setDcProviderCode] = useState("");
	const [dcCity, setDcCity] = useState("");
	const [dcDisplayName, setDcDisplayName] = useState("");
	const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);
	const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
	const { tenantId, partnerId } = useAppSelector((state) => state.selection);
	const isViewingAllCustomers = tenantId === partnerId;

	// ========= 3) View modal state =========
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedDataCenter, setSelectedDataCenter] =
		useState<DataCenter | null>(null);
	const [isViewLoading, setIsViewLoading] = useState(false);

	// ========= 4) Delete confirmation state =========
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [dcToDelete, setDcToDelete] = useState<DataCenter | null>(null);

	// ========== Auth0 para obtener token y llamar a nuestros endpoints ==========
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

	//Provider Dropdown Handling logic
	// 1) Convert provider objects to an array of strings for the dropdown
	const providerNames = providerOptions.map((opt) => opt.name);

	// 2) Find the name associated with the currently selected provider ID
	function getProviderNameById(providerId: string): string {
		const match = providerOptions.find((opt) => String(opt.id) === providerId);
		return match ? match.name : "";
	}

	// 3) When the user picks a name, find its ID and store it
	function handleSelectProviderName(selectedName: string) {
		const match = providerOptions.find((opt) => opt.name === selectedName);
		setDcProvider(match ? String(match.id) : "");
	}

	const suggestedKeys = useMemo(() => {
	const headers = baseColumns
		.map((c) => c.header)
		.filter(Boolean) as string[];

	if (isViewingAllCustomers && !headers.includes("Customer")) {
		headers.splice(1, 0, "Customer");
	}
	return headers;
	}, [baseColumns, isViewingAllCustomers]);

	// ------------------------------------------------------------------------------------
	// useEffect: llama GET /v1/dataCenters/list
	// ------------------------------------------------------------------------------------
	useEffect(() => {
		if (isLoading) return;
		if (!isAuthenticated) return;

		const fetchDataCentersList = async () => {
			try {
				setIsFetching(true); // Activa spinner
				const token = await getAccessTokenSilently();
				const response = await getDataCenters(token);
				const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
				const shapedData = shapeDataCenterData(raw);
				setDataCenters(shapedData);
			} catch (err) {
				console.error("Error fetching data centers:", err);
			} finally {
				setIsFetching(false); // Desactiva spinner
			}
		};
		const fetchProviderOptions = async () => {
			try {
				const token = await getAccessTokenSilently();
				// getProviderIds should return an array
				const providerResp = await getProviderIds(token);
				setProviderOptions(providerResp.data.data); // store them in state
			} catch (error) {
				console.error("Error fetching provider IDs:", error);
			}
		};

		fetchProviderOptions();
		fetchDataCentersList();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	function makeDataCenterFields(): FilterField<DataCenter>[] {
		return [
		{ label: "Datacenter ID", key: "datacenterid", type: "string", accessor: r => r.datacenterId, inFreeText: true },
		{ label: "Name",          key: "name",         type: "string", accessor: r => r.name,         inFreeText: true },
		{ label: "Direction",     key: "direction",    type: "string", accessor: r => r.direction,    inFreeText: true },
		{ label: "Status",        key: "status",       type: "string", accessor: r => r.status,       inFreeText: true },
		{ label: "Capacity (KW)", key: "capacity",     type: "number", accessor: r => r.capacityKW,     inFreeText: true },
		];
	}

	const dcFields = useMemo(() => makeDataCenterFields(), []);
	const fieldById = useMemo(() => {
	const m = new Map<string, FilterField<DataCenter>>();
	for (const f of dcFields) {
		const id = (f.key ?? labelId(f.label)).toLowerCase();
		m.set(id, f);
	}
	return m;
	}, [dcFields]);

	const keyMap = useMemo(() => {
	const m = new Map<string, string>();
	for (const f of dcFields) {
		const id = (f.key ?? labelId(f.label)).toLowerCase();
		const label = f.label.toLowerCase();
		m.set(id, id);
		m.set(label, id);
	}
	return m;
	}, [dcFields]);

	const normalizedFilters = useMemo<ParsedFilter[]>(() => {
	return activeFilters.map((f) => {
		// keep live op if present; otherwise parse
		const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

		// Canonicalize key to our id
		if (token.key) {
		const k = token.key.trim().toLowerCase();
		token.key = keyMap.get(k) ?? labelId(token.key);
		}

		// If this is a STRING field and user quoted the value -> exact match
		const field = token.key ? fieldById.get(token.key) : undefined;
		if (field?.type === "string" && token.value) {
		const quoted = token.value.match(/^"(.*)"$/);
		if (quoted) {
			token.value = quoted[1]; // strip quotes
			token.op = "=";
		} else if (!token.op) {
			token.op = ":"; // default contains
		}
		}

		return token;
	});
	}, [activeFilters, keyMap, fieldById]);

	const predicate = useMemo(
		() => buildPredicate<DataCenter>(normalizedFilters, dcFields),
		[normalizedFilters, dcFields]
	);
	
	const filteredData = useMemo(
		() => dataCenters.filter(predicate),
		[dataCenters, predicate]
	);
	


	// ------------------------------------------------------------------------------------
	// Handle ADD
	// ------------------------------------------------------------------------------------
	const handleAddDC = () => {
		setEditMode(false);
		// Limpiar campos
		setDcId("");
		setDcName("");
		setDcDirection("");
		setDcCapacity("");
		setDcStatus("Active");
		setDcGps("");
		setDcPhase("");
		setDcCage("");
		setDcRackId("");
		setDcRackCapacity("");
		setDcPatchPanelODF("");
		setDcRackSecurityPin("");
		setIsModalOpen(true);
		setDcVisible(true);
		setDcProvider("");
		setDcProviderCode("");
		setDcCity("");
		setDcDisplayName("");
	};

	// ------------------------------------------------------------------------------------
	// Handle EDIT
	// ------------------------------------------------------------------------------------
	const handleEditDC = async (dc: DataCenter) => {
		setEditMode(true);
		setIsModalOpen(true);
		setIsEditLoading(true);
		setEditDetailData(null);
		
		// Set form fields from row data
		setDcId(dc.datacenterId ?? "");
		setDcName(dc.name ?? "");
		setDcDirection(dc.direction ?? "");
		setDcCapacity(String(dc.capacityKW ?? ""));
		setDcStatus(dc.status ?? "");
		setDcGps(dc.gps ?? "");
		setDcPhase(dc.phase ?? "");
		setDcCage(dc.cage ?? "");
		setDcRackId(dc.rackId ?? "");
		setDcRackCapacity(String(dc.rackCapacity ?? ""));
		setDcPatchPanelODF(dc.patchPanelODF ?? "");
		setDcRackSecurityPin(dc.rackSecurityPin ?? "");
		setDcVisible(dc.visible ?? false);
		setDcProvider(dc.providerId ?? "");
		setDcProviderCode(dc.providerCode ?? "");
		setDcCity(dc.city ?? "");
		setDcDisplayName(dc.displayName ?? "");
		
		// Fetch detail data
		try {
			const token = await getAccessTokenSilently();
			const dcId = String(dc.id);
			const response = await getDataCenterId(token, dcId);
			const raw = (response as any)?.data?.data ?? (response as any)?.data;
			
			if (raw) {
				const shapedData: DataCenter = {
					id: raw.id ?? dc.id,
					name: raw.name ?? dc.name,
					tag: Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : (dc.tag ?? []),
					isDraft: Boolean(raw.isDraft ?? dc.isDraft),
					subscriptionId: raw.subscriptionId ?? dc.subscriptionId,
					terms: raw.terms ?? dc.terms,
					mbps: raw.mbps ?? dc.mbps,
					tenantNameDisplay: raw.kontrolEdgePort?.tenant?.nameDisplay ?? dc.tenantNameDisplay,
					automaticVlan: Boolean(raw.automaticVlan ?? dc.automaticVlan),
					vlan: raw.vlan ?? dc.vlan,
					kontrolEdgePortId: raw.kontrolEdgePortId,
					redundancy: Boolean(raw.redundancy),
					countryCode: raw.countryCode,
					autoRenew: Boolean(raw.autoRenew),
					p2pAddressing: raw.p2pAddressing,
					bgpConfig: raw.bgpConfig,
					kontrolEdgeLagId: raw.kontrolEdgeLagId,
					latencyTargets: raw.latencyTargets,
					kontrolEdgePortIdA: raw.kontrolEdgePortIdA,
					kontrolEdgePortIdB: raw.kontrolEdgePortIdB,
					createdAt: raw.createdAt,
					updatedAt: raw.updatedAt,
					odooMetadata: raw.odooMetadata,
					kontrolEdgePort: raw.kontrolEdgePort,
					kontrolEdgePortA: raw.kontrolEdgePortA,
					kontrolEdgePortB: raw.kontrolEdgePortB,
					endpointBType: raw.endpointBType,
					endpointBId: raw.endpointBId,
					endpointBSdwanHub: raw.endpointBSdwanHub,
					endpointBFwaas: raw.endpointBFwaas,
					datacenterId: raw.subscriptionId ?? String(raw.id ?? "") ?? dc.datacenterId,
					direction: raw.kontrolEdgePort?.tenant?.nameDisplay ?? dc.direction,
					gps: raw.gps ?? dc.gps ?? "",
					capacityKW: typeof raw.mbps === "number" ? raw.mbps : Number(raw.mbps) || dc.capacityKW || 0,
					phase: raw.phase ?? dc.phase ?? "",
					cage: raw.cage ?? dc.cage ?? "",
					rackId: raw.rackId ?? dc.rackId ?? "",
					rackCapacity: Number(raw.rackCapacity) || dc.rackCapacity || 0,
					patchPanelODF: raw.patchPanelODF ?? dc.patchPanelODF ?? "",
					rackSecurityPin: raw.rackSecurityPin ?? dc.rackSecurityPin ?? "",
					status: raw.isDraft ? "Draft" : "Active",
					visible: raw.visible ?? dc.visible ?? true,
					providerId: raw.providerId ?? dc.providerId ?? "",
					providerCode: raw.providerCode ?? dc.providerCode ?? "",
					city: raw.countryCode ?? raw.city ?? dc.city ?? "",
					displayName: raw.name ?? raw.kontrolEdgePort?.name ?? dc.displayName ?? "",
				};
				setEditDetailData(shapedData);
			}
		} catch (error) {
			console.error("Error fetching data center details for edit:", error);
		} finally {
			setIsEditLoading(false);
		}
	};

	// ------------------------------------------------------------------------------------
	// Handle SAVE (ADD / UPDATE)
	// ------------------------------------------------------------------------------------
	const handleSaveDataCenter = async () => {
		try {
			setIsFetching(true); // spinner durante ADD  UPDATE
			const token = await getAccessTokenSilently();
			const payload: AddDataCenterPayload = {
				datacenterId: dcId,
				name: dcName,
				direction: dcDirection,
				gps: dcGps,
				status: dcStatus,
				capacityKW: dcCapacity,
				phase: dcPhase,
				cage: dcCage,
				rackId: dcRackId,
				rackCapacity: dcRackCapacity,
				patchPanelODF: dcPatchPanelODF,
				rackSecurityPin: dcRackSecurityPin,
				visible: dcVisible,
				providerId: dcProvider,
				providerCode: dcProviderCode,
				city: dcCity,
				displayName: dcDisplayName,
			};
			if (editMode) {
				// Update (PUT)
				await updateDataCenter(token, dcId, payload);

				setDataCenters((prev) =>
					prev.map((item) =>
						item.datacenterId === dcId
							? {
									...item,
									datacenterId: dcId,
									name: dcName,
									direction: dcDirection,
									capacityKW: Number(dcCapacity) || 0,
									status: dcStatus,
									gps: dcGps,
									phase: dcPhase,
									cage: dcCage,
									rackId: dcRackId,
									rackCapacity: Number(dcRackCapacity) || 0,
									patchPanelODF: dcPatchPanelODF,
									rackSecurityPin: dcRackSecurityPin,
									visible: dcVisible,
									providerId: dcProvider,
									providerCode: dcProviderCode,
									city: dcCity,
									displayName: dcDisplayName,
								}
							: item
					)
				);
			} else {
				// Add (POST)
				await addDataCenter(token, payload);

				setDataCenters((prev) => [
					...prev,
					{
						id: dcId,
						datacenterId: dcId,
						name: dcName,
						direction: dcDirection,
						capacityKW: Number(dcCapacity) || 0,
						status: dcStatus,
						gps: dcGps,
						phase: dcPhase,
						cage: dcCage,
						rackId: dcRackId,
						rackCapacity: Number(dcRackCapacity) || 0,
						patchPanelODF: dcPatchPanelODF,
						rackSecurityPin: dcRackSecurityPin,
						visible: dcVisible,
						providerId: dcProvider,
						providerCode: dcProviderCode,
						city: dcCity,
						displayName: dcDisplayName,
						tag: [],
						isDraft: false,
						subscriptionId: dcId,
						terms: "",
						mbps: "",
						tenantNameDisplay: "",
						automaticVlan: false,
						vlan: "",
					},
				]);
			}
		} catch (error) {
			console.error("Error saving Data Center:", error);
		} finally {
			setIsFetching(false); // spinner off
		}

		setIsModalOpen(false);
	};

	// ------------------------------------------------------------------------------------
	// Handle VIEW
	// ------------------------------------------------------------------------------------
	const handleViewDC = async (dc: DataCenter) => {
		setIsViewModalOpen(true);
		setIsViewLoading(true);
		setSelectedDataCenter(null);
		
		try {
			const token = await getAccessTokenSilently();
			// Use subscriptionId as the ID for the detail request
			const dcId = String(dc.id);
			const response = await getDataCenterId(token, dcId);
			const raw = (response as any)?.data?.data ?? (response as any)?.data;
			
			if (raw) {
				// Shape the response data to match DataCenter interface
				const shapedData: DataCenter = {
					id: raw.id ?? dc.id,
					name: raw.name ?? dc.name,
					tag: Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : (dc.tag ?? []),
					isDraft: Boolean(raw.isDraft ?? dc.isDraft),
					subscriptionId: raw.subscriptionId ?? dc.subscriptionId,
					terms: raw.terms ?? dc.terms,
					mbps: raw.mbps ?? dc.mbps,
					tenantNameDisplay: raw.kontrolEdgePort?.tenant?.nameDisplay ?? dc.tenantNameDisplay,
					automaticVlan: Boolean(raw.automaticVlan ?? dc.automaticVlan),
					vlan: raw.vlan ?? dc.vlan,
					// Detail response fields
					kontrolEdgePortId: raw.kontrolEdgePortId,
					redundancy: Boolean(raw.redundancy),
					countryCode: raw.countryCode,
					autoRenew: Boolean(raw.autoRenew),
					p2pAddressing: raw.p2pAddressing,
					bgpConfig: raw.bgpConfig,
					kontrolEdgeLagId: raw.kontrolEdgeLagId,
					latencyTargets: raw.latencyTargets,
					kontrolEdgePortIdA: raw.kontrolEdgePortIdA,
					kontrolEdgePortIdB: raw.kontrolEdgePortIdB,
					createdAt: raw.createdAt,
					updatedAt: raw.updatedAt,
					odooMetadata: raw.odooMetadata,
					kontrolEdgePort: raw.kontrolEdgePort,
					kontrolEdgePortA: raw.kontrolEdgePortA,
					kontrolEdgePortB: raw.kontrolEdgePortB,
					// Legacy fields
					datacenterId: raw.subscriptionId ?? String(raw.id ?? "") ?? dc.datacenterId,
					direction: raw.kontrolEdgePort?.tenant?.nameDisplay ?? dc.direction,
					gps: raw.gps ?? dc.gps ?? "",
					capacityKW: typeof raw.mbps === "number" ? raw.mbps : Number(raw.mbps) || dc.capacityKW || 0,
					phase: raw.phase ?? dc.phase ?? "",
					cage: raw.cage ?? dc.cage ?? "",
					rackId: raw.rackId ?? dc.rackId ?? "",
					rackCapacity: Number(raw.rackCapacity) || dc.rackCapacity || 0,
					patchPanelODF: raw.patchPanelODF ?? dc.patchPanelODF ?? "",
					rackSecurityPin: raw.rackSecurityPin ?? dc.rackSecurityPin ?? "",
					status: raw.isDraft ? "Draft" : "Active",
					visible: raw.visible ?? dc.visible ?? true,
					providerId: raw.providerId ?? dc.providerId ?? "",
					providerCode: raw.providerCode ?? dc.providerCode ?? "",
					city: raw.countryCode ?? raw.city ?? dc.city ?? "",
					displayName: raw.name ?? raw.kontrolEdgePort?.name ?? dc.displayName ?? "",
				};
				setSelectedDataCenter(shapedData);
			}
		} catch (error) {
			console.error("Error fetching data center details:", error);
			// Fallback to the row data if the detail request fails
			setSelectedDataCenter(dc);
		} finally {
			setIsViewLoading(false);
		}
	};

	// ------------------------------------------------------------------------------------
	// Handle DELETE
	// ------------------------------------------------------------------------------------
	const handleDeleteClick = (dc: DataCenter) => {
		setDcToDelete(dc);
		setIsAlertOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!dcToDelete) return;
		try {
			setIsFetching(true); // spinner durante DELETE
			const token = await getAccessTokenSilently();
			await deleteDataCenter(token, dcToDelete.datacenterId ?? "");

			setDataCenters((prev) =>
				prev.filter((item) => item.datacenterId !== (dcToDelete.datacenterId ?? ""))
			);
			setDcToDelete(null);
			setIsAlertOpen(false);
		} catch (error) {
			console.error("Error deleting data center:", error);
		} finally {
			setIsFetching(false); // spinner off
		}
	};

	const handleCancelDelete = () => {
		setDcToDelete(null);
		setIsAlertOpen(false);
	};

	// ------------------------------------------------------------------------------------
	// Override "Actions"
	// ------------------------------------------------------------------------------------
	const columns = [
		...baseColumns,
		{
			header: "Actions",
			accessor: "name" as keyof DataCenter,
			renderCell: (row: DataCenter) => (
				<div className="flex items-center gap-2">
					{/* View Button */}
					<button
						className="
							flex items-center justify-center
							border border-semantic-border-secondary
							rounded
							text-semantic-text-secondary
							hover:bg-semantic-state-active
							w-10 h-10
						"
						onClick={(e) => {
							e.stopPropagation();
							handleViewDC(row);
						}}
						title="View details"
					>
						<IconEye className="w-5 h-5" />
					</button>

					{/* Edit Button */}
					<button
						className="
							flex items-center justify-center
							border border-semantic-border-secondary
							rounded
							text-semantic-text-secondary
							hover:bg-semantic-state-active
							w-10 h-10
						"
						onClick={(e) => {
							e.stopPropagation();
							handleEditDC(row);
						}}
						title="Edit"
					>
						<IconEdit className="w-9 h-9" />
					</button>

					{/* Delete Button */}
					<button
						className="
							flex items-center justify-center
							p-2
							border border-semantic-border-secondary
							rounded
							bg-red-50
							text-red-600
							hover:bg-red-100
							w-10 h-10
						"
						onClick={(e) => {
							e.stopPropagation();
							handleDeleteClick(row);
						}}
						title="Delete"
					>
						<IconDelete className="w-5 h-5" />
					</button>
				</div>
			),
		},
	];
	// ------------------------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------------------------
	return (
		<GenericCard
			isCollapsible={false}
		>
			{/* Contenido principal siempre */}
			{/* Header */}
			<div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
				<div className="flex items-end justify-between">
					<div className="w-[520px] my-5">
						<GenericSearchbar
							placeholder="Find by attribute or value"
							suggestedKeys={suggestedKeys}
							onFiltersChange={setActiveFilters}
						/>
					</div>
					<div className="mb-10">
						<GenericButton
							label={
								<div className="flex items-center gap-2">
									<IconPlus className="w-5 h-5" />
									<span>Add new data center</span>
								</div>
							}
							variant="brand"
							size="md"
							onClick={handleAddDC}
						/>
					</div>
				</div>
			</div>
			

			{/* Table */}
			<div className="px-5 mb-5">
				{/* The table's own slot in skeleton. The overlay this replaces covered
				    the rail and the views bar for the whole wait (CTX-1028). */}
				{isFetching ? (
					<TableSkeleton columns={columns.length} rows={5} />
				) : (
					<GenericTable
						columns={columns}
						data={filteredData}
						initialRowsPerPage={5}
						rowsPerPageOptions={[5, 10]}
						tableClassName="bg-[var(--semantic-bg-primary)]"
						width="100%"
					/>
				)}
			</div>

			{/* ====================== Modal Add / Edit ====================== */}
			<DcEditModal
				isOpen={isModalOpen}
				editMode={editMode}
				isLoading={isEditLoading}
				editDetailData={editDetailData}
				dcId={dcId}
				dcName={dcName}
				dcDirection={dcDirection}
				dcCapacity={dcCapacity}
				dcStatus={dcStatus}
				dcGps={dcGps}
				dcPhase={dcPhase}
				dcCage={dcCage}
				dcRackId={dcRackId}
				dcRackCapacity={dcRackCapacity}
				dcPatchPanelODF={dcPatchPanelODF}
				dcRackSecurityPin={dcRackSecurityPin}
				dcVisible={dcVisible}
				dcProvider={dcProvider}
				dcProviderCode={dcProviderCode}
				dcCity={dcCity}
				dcDisplayName={dcDisplayName}
				providerOptions={providerOptions}
				onClose={() => setIsModalOpen(false)}
				onSave={handleSaveDataCenter}
				onDcIdChange={setDcId}
				onDcNameChange={setDcName}
				onDcDirectionChange={setDcDirection}
				onDcCapacityChange={setDcCapacity}
				onDcStatusChange={setDcStatus}
				onDcGpsChange={setDcGps}
				onDcPhaseChange={setDcPhase}
				onDcCageChange={setDcCage}
				onDcRackIdChange={setDcRackId}
				onDcRackCapacityChange={setDcRackCapacity}
				onDcPatchPanelODFChange={setDcPatchPanelODF}
				onDcRackSecurityPinChange={setDcRackSecurityPin}
				onDcVisibleChange={setDcVisible}
				onDcProviderChange={setDcProvider}
				onDcProviderCodeChange={setDcProviderCode}
				onDcCityChange={setDcCity}
				onDcDisplayNameChange={setDcDisplayName}
				getProviderNameById={getProviderNameById}
				handleSelectProviderName={handleSelectProviderName}
			/>

			{/* ====================== Modal View (read-only) ====================== */}
			<DcViewModal
				isOpen={isViewModalOpen}
				isLoading={isViewLoading}
				dataCenter={selectedDataCenter}
				onClose={() => setIsViewModalOpen(false)}
			/>

			{/* ====================== ALERT: confirm Delete ====================== */}
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Are you sure?"
				description="This action cannot be undone. This will permanently delete the data center from the inventory."
				cancelLabel="Cancel"
				confirmLabel="Delete"
				iconWidthClass="w-20 h-12"
			/>
		</GenericCard>
	);
}
